// src/Contexts/ActiveJobContext.js
//
// One selected target job, shared by every job-aware page:
//
//   /jobs/saved (tracker)      sets it from a card's Tailor / Cover Letter /
//                              Interview Prep buttons
//   /jobs/tailor               reads it when there is no router state
//   /create-resume/resume      JobTailorPanel prefills the posting from it
//   /cover-letter              CoverLetterForm targets the letter at it
//   /interview                 selects that job's prep session / prefills
//   /applications/answers      prefills the "drafting for" role + company
//
// THIS MODULE IS DELIBERATELY FIREBASE-FREE. It holds only the context
// object, the hook and the inert default, so a component that reads the
// context can be unit-tested without initialising the Firebase app. The
// provider - the half that talks to Firestore - lives in ActiveJobProvider.js
// and is mounted once, in Routers.js.
//
// WITHOUT A PROVIDER the hook returns an inert value (null job, no-op
// setters), so a component rendered in a unit test - or on a route that is
// not wrapped - behaves exactly as it did before this context existed.

import { createContext, useContext } from "react";

const noop = () => {};

/**
 * @typedef {Object} ActiveJob
 * @property {string}  jobId                 saved-jobs item id; "" for a pasted posting
 * @property {string}  title
 * @property {string}  company
 * @property {string}  location
 * @property {string}  description           clamped to SAVED_JOB_DESCRIPTION_CHARS
 * @property {boolean} descriptionTruncated
 * @property {string}  applyUrl
 * @property {string}  jobUrl
 * @property {string}  source
 * @property {string}  setAt                 ISO timestamp of the selection
 */

export const INERT_ACTIVE_JOB = {
  /** @type {ActiveJob|null} */
  activeJob: null,
  activeJobId: "",
  hasActiveJob: false,
  // True while the provider is still reading Firestore after sign-in. Pages
  // that show a "pick a job first" empty state wait for this to clear.
  isHydrating: false,
  /** (job) => normalized ActiveJob | null. Accepts any job-like object. */
  setActiveJob: noop,
  clearActiveJob: noop,
};

export const ActiveJobContext = createContext(INERT_ACTIVE_JOB);

export const useActiveJob = () => useContext(ActiveJobContext);

export default ActiveJobContext;
